<script lang="ts">
  import {
    StructuredList,
    StructuredListBody,
    StructuredListCell,
    StructuredListHead,
    StructuredListRow,
    TooltipDefinition,
  } from 'carbon-components-svelte';

  import { DataInventoryServiceIdToName, DEFAULT_SLA_MARKET_DAY_OFFSET, getSlaMarketDayOffsetLabel } from '../../../../shared/constants';

  import type { DataFeedInventorySummaryRow } from '../../../services';
  import type { SlaTaskAssociation, SlaTaskFormValues } from '../../../../shared/types/sla-task-types';

  export let values: Partial<SlaTaskFormValues> = {};
  export let availableFeeds: DataFeedInventorySummaryRow[] = [];

  const serviceIdToName = (id: number): string => DataInventoryServiceIdToName[id as keyof typeof DataInventoryServiceIdToName];

  $: associations = (Array.isArray(values.associations) ? values.associations : []) as SlaTaskAssociation[];
  $: taskRows = [
    { label: 'Task Name', value: values.friendlyName || '—' },
    { label: 'Description', value: values.description || '—' },
    { label: 'Internal Only', value: values.internalOnly ? 'Yes' : 'No' },
    { label: 'Time', value: values.time || '—' },
    { label: 'Time Zone', value: values.timezone || '—' },
  ];
  $: associationRows = availableFeeds
    .filter((feed) => associations.some((association) => association.feedId === feed.id))
    .map((feed) => ({
      feedName: feed.name,
      services: associations
        .filter((association) => association.feedId === feed.id)
        .map((association) => {
          const name = serviceIdToName(association.serviceId);
          return association.marketDayOffset === DEFAULT_SLA_MARKET_DAY_OFFSET
            ? name
            : `${name} (${getSlaMarketDayOffsetLabel(association.marketDayOffset)})`;
        }),
    }));
</script>

<div class="preview-grid">
  <section class="preview-column">
    <h4>Task and Schedule</h4>
    <StructuredList condensed flush>
      <StructuredListHead>
        <StructuredListRow head>
          <StructuredListCell head>Attribute</StructuredListCell>
          <StructuredListCell head>Value</StructuredListCell>
        </StructuredListRow>
      </StructuredListHead>
      <StructuredListBody>
        {#each taskRows as row}
          <StructuredListRow>
            <StructuredListCell noWrap>{row.label}</StructuredListCell>
            <StructuredListCell>{row.value}</StructuredListCell>
          </StructuredListRow>
        {/each}
      </StructuredListBody>
    </StructuredList>
  </section>

  <section class="preview-column associations-column">
    <h4>Associations</h4>
    <div class="associations-list">
      <StructuredList condensed flush>
        <StructuredListHead>
          <StructuredListRow head>
            <StructuredListCell head>Feed</StructuredListCell>
            <StructuredListCell head>Services</StructuredListCell>
          </StructuredListRow>
        </StructuredListHead>
        <StructuredListBody>
          {#if associationRows.length}
            {#each associationRows as row}
              <StructuredListRow>
                <StructuredListCell noWrap>{row.feedName}</StructuredListCell>
                <StructuredListCell>
                  <div class="services-cell">
                    {#if row.services.length}
                      <TooltipDefinition direction="top" tooltipText={row.services.join(', ')} portalTooltip>
                        {row.services.length}
                      </TooltipDefinition>
                    {/if}
                  </div>
                </StructuredListCell>
              </StructuredListRow>
            {/each}
          {:else}
            <StructuredListRow>
              <StructuredListCell>No associations selected.</StructuredListCell>
            </StructuredListRow>
          {/if}
        </StructuredListBody>
      </StructuredList>
    </div>
  </section>
</div>

<style>
  .preview-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 1rem;
  }

  .preview-column {
    min-width: 0;
  }

  .preview-column :global(.bx--structured-list) {
    margin-bottom: 0;
  }

  .associations-list {
    max-height: 22rem;
    overflow-y: auto;
    padding-right: 0.25rem;
  }

  .services-cell {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  }

  @media (max-width: 900px) {
    .preview-grid {
      grid-template-columns: 1fr;
    }

    .associations-list {
      max-height: none;
    }
  }
</style>
